import { useEffect } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Tailwind max-width 类，默认 max-w-2xl */
  maxWidth?: string;
}

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "max-w-2xl"
}: ModalProps) {
  // Esc 关闭 + 锁定 body 滚动
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <div
        className={`bg-paper rounded-t-2xl sm:rounded-2xl shadow-cardHover ${maxWidth} w-full max-h-[92vh] sm:max-h-[88vh] overflow-hidden flex flex-col border border-hairline`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-hairline flex items-center justify-between flex-none bg-paper">
          <div>
            <h2 className="text-base font-semibold text-ink leading-tight">{title}</h2>
            {subtitle && <p className="text-xs text-soft mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md text-soft hover:text-ink hover:bg-chip flex items-center justify-center"
            aria-label="关闭"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M6 18L18 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>
        <main className="flex-1 overflow-y-auto p-6 bg-page">{children}</main>
      </div>
    </div>
  );
}
